import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ThemeTransition from "@/components/ThemeTransition";
import LandingPage from "@/pages/LandingPage";
import InventoryPage from "@/pages/InventoryPage";
import VehicleDetailPage from "@/pages/VehicleDetailPage";
import FinancingPage from "@/pages/FinancingPage";
import TradeInPage from "@/pages/TradeInPage";
import PreQualificationPage from "@/pages/PreQualificationPage";
import LoanCalculatorPage from "@/pages/LoanCalculatorPage";
import ScheduleServicePage from "@/pages/ScheduleServicePage";
import AdminLogin from "@/pages/AdminLogin";
import AdminAcceptInvitePage from "@/pages/AdminAcceptInvitePage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminProfilePage from "@/pages/AdminProfilePage";
import AdminVerifyEmailPage from "@/pages/AdminVerifyEmailPage";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminInsightsPage from "@/pages/AdminInsightsPage";
import DealsPage from "@/pages/DealsPage";
import DealDetailPage from "@/pages/DealDetailPage";
import DocumentsPage from "@/pages/DocumentsPage";
import OperationsPage from "@/pages/OperationsPage";
import TrackLeads from "@/pages/TrackLeads";
import LeadDetailPage from "@/pages/LeadDetailPage";
import ServiceRequests from "@/pages/ServiceRequests";
import ServiceRequestDetailPage from "@/pages/ServiceRequestDetailPage";
import AdminSchedulePage from "@/pages/AdminSchedulePage";
import AdminImaginePage from "@/pages/AdminImaginePage";
import AdminOutreachAgentPage from "@/pages/AdminOutreachAgentPage";
import ServiceQuoteApprovalPage from "@/pages/ServiceQuoteApprovalPage";
import CustomerInfoRequestPage from "@/pages/CustomerInfoRequestPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsPage from "@/pages/TermsPage";
import SoldVehiclesPage from "@/pages/SoldVehiclesPage";
import SoldVehicleDetailPage from "@/pages/SoldVehicleDetailPage";
import AdminVehicleDetailPage from "@/pages/AdminVehicleDetailPage";
import MobilePhotoUploadPage from "@/pages/MobilePhotoUploadPage";
import { setDocumentTitle } from "@/utils/pageTitle";
import { trackPageView } from "@/utils/ga4";
import ChatWidget from "@/components/ChatWidget";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const id = decodeURIComponent((hash || "").replace(/^#/, ""));
    if (!id) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 40;

    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        window.setTimeout(tryScroll, 50);
      }
    };

    const timer = window.setTimeout(tryScroll, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [pathname, hash]);
  return null;
}

function DocumentTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/inventory/") && pathname !== "/inventory") {
      return;
    }
    setDocumentTitle(pathname);
    trackPageView(pathname);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <div className="App min-h-screen bg-[#050505] dark:bg-[#050505] light:bg-[#FAFAFA]">
            <BrowserRouter>
              <ScrollToTop />
              <DocumentTitle />
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/inventory" element={<InventoryPage />} />
                <Route path="/inventory/:vehicleId" element={<VehicleDetailPage />} />
                <Route path="/financing" element={<FinancingPage />} />
                <Route path="/trade-in" element={<TradeInPage />} />
                <Route path="/prequalification" element={<PreQualificationPage />} />
                <Route path="/loan-calculator" element={<LoanCalculatorPage />} />
                <Route path="/schedule-service" element={<ScheduleServicePage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/mobile-photos/:token" element={<MobilePhotoUploadPage />} />
                <Route path="/customer-info/:token" element={<CustomerInfoRequestPage />} />
                <Route path="/service-quote/:token" element={<ServiceQuoteApprovalPage />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/accept-invite" element={<AdminAcceptInvitePage />} />
                <Route path="/admin/verify-email" element={<AdminVerifyEmailPage />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminInsightsPage />} />
                  <Route path="inventory" element={<AdminDashboard />} />
                  <Route path="vehicles/:vehicleId" element={<AdminVehicleDetailPage />} />
                  <Route path="sold-vehicles" element={<SoldVehiclesPage />} />
                  <Route path="sold-vehicles/:vehicleId" element={<SoldVehicleDetailPage />} />
                  <Route path="deals" element={<DealsPage />} />
                  <Route path="deals/:dealId" element={<DealDetailPage />} />
                  <Route path="documents" element={<DocumentsPage />} />
                  <Route path="operations" element={<OperationsPage />} />
                  <Route path="leads" element={<TrackLeads />} />
                  <Route path="leads/:leadId" element={<LeadDetailPage />} />
                  <Route path="service-requests" element={<ServiceRequests />} />
                  <Route path="service-requests/:requestId" element={<ServiceRequestDetailPage />} />
                  <Route path="imagine" element={<AdminImaginePage />} />
                  <Route path="outreach" element={<AdminOutreachAgentPage />} />
                  <Route path="schedule" element={<AdminSchedulePage />} />
                  <Route path="users" element={<AdminUsersPage />} />
                  <Route path="profile" element={<AdminProfilePage />} />
                </Route>
              </Routes>
              <ChatWidget />
            </BrowserRouter>
            <Toaster position="bottom-right" richColors />
            {/* Cinematic full-bleed theme-transition overlay (renders above everything) */}
            <ThemeTransition />
          </div>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
